import { type ReactElement, useId, useState } from 'react';
import { Link } from 'react-router';
import { useMeQuery } from '@/entities/user';
import { PasswordChangeRequest } from '@/features/auth';
import { BusinessProfileForm } from '@/features/business-profile';
import { EmailChangeForm, ProfileNameForm } from '@/features/profile-edit';
import { MY_WITHDRAW_PATH } from '@/shared/config';
import { formatDate } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { ProfileSection } from './profile-section';
import { VerificationRow } from './verification-row';

/**
 * 회원정보(users.me) — 기본 정보(이메일·이름 변경·가입일), 인증 상태(이메일·본인인증), 비밀번호 재설정 메일.
 * 서버가 본인인증을 쓰지 않으면(identityRequired=false) 본인인증 행을 그리지 않는다.
 * 이름 변경은 users.updateProfile, 비밀번호는 별도 변경 API 가 없어 auth.requestPasswordReset 메일로 바꾼다.
 */
export const MyProfilePanel = (): ReactElement => {
  const me = useMeQuery();
  const headingId = useId();
  const [isEmailFormOpen, setIsEmailFormOpen] = useState<boolean>(false);

  return (
    <section aria-labelledby={headingId} className="flex min-w-0 flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h2
          id={headingId}
          className="text-2xl leading-tight font-bold tracking-[-0.02em] sm:text-[1.75rem]"
        >
          회원정보
        </h2>
        <p className="text-sm text-muted-foreground">
          계정 정보와 인증 상태를 확인하고 관리합니다.
        </p>
      </div>

      {me.isPending ? (
        <p role="status" className="py-10 text-sm text-muted-foreground">
          회원정보를 불러오는 중입니다.
        </p>
      ) : me.isError ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-4 border-y border-foreground/10 py-16 text-center"
        >
          <p className="text-sm text-destructive">회원정보를 불러오지 못했습니다.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void me.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-10">
          <ProfileSection
            title="기본 정보"
            description="이름은 견적서 담당자 기본값으로도 쓰입니다."
          >
            <div className="flex flex-col gap-6">
              <dl className="divide-y divide-foreground/10 border-y border-foreground/10 text-sm">
                <div className="flex gap-4 py-3">
                  <dt className="w-28 shrink-0 text-muted-foreground">이메일</dt>
                  <dd className="flex min-w-0 flex-1 items-center justify-between gap-3">
                    <span className="min-w-0 wrap-anywhere">{me.data.email}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      aria-expanded={isEmailFormOpen}
                      onClick={() => setIsEmailFormOpen((open) => !open)}
                    >
                      변경
                    </Button>
                  </dd>
                </div>
                <div className="flex gap-4 py-3">
                  <dt className="w-28 shrink-0 text-muted-foreground">가입일</dt>
                  <dd className="tabular-nums">{formatDate(me.data.createdAt)}</dd>
                </div>
              </dl>
              {isEmailFormOpen ? (
                <EmailChangeForm currentEmail={me.data.email} hasPassword={me.data.hasPassword} />
              ) : null}
              <ProfileNameForm key={me.data.id} initialName={me.data.name} />
            </div>
          </ProfileSection>

          <ProfileSection
            title="사업자 정보"
            description="세금계산서 발행에 사용할 정보를 저장합니다."
          >
            <BusinessProfileForm />
          </ProfileSection>

          <ProfileSection
            title="인증 상태"
            description={
              me.data.identityRequired
                ? '견적·디자인 저장은 두 인증을 마친 계정만 쓸 수 있습니다.'
                : '견적·디자인 저장은 이메일 인증을 마친 계정만 쓸 수 있습니다.'
            }
          >
            <dl className="divide-y divide-foreground/10 border-y border-foreground/10">
              <VerificationRow label="이메일 인증" verifiedAt={me.data.emailVerifiedAt} />
              {me.data.identityRequired ? (
                <VerificationRow label="본인인증" verifiedAt={me.data.identityVerifiedAt} />
              ) : null}
            </dl>
          </ProfileSection>

          <ProfileSection
            title="비밀번호"
            description="가입한 이메일로 재설정 링크를 보내 드립니다."
          >
            <PasswordChangeRequest
              email={me.data.email}
              emailVerified={me.data.emailVerifiedAt !== null}
            />
          </ProfileSection>

          <div className="border-t border-foreground/10 pt-6">
            <Link
              to={MY_WITHDRAW_PATH}
              className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              회원 탈퇴
            </Link>
          </div>
        </div>
      )}
    </section>
  );
};
